import type {
  CentreSegment,
  CreditNoteDepartmentNode,
  CreditNoteDetail,
  CreditNoteMonth,
  CreditNoteNode,
  CreditNotes,
  CreditNotesByHierarchy,
  CreditNoteUnitNode,
  CurrencyData,
  Kpis,
  KpiSet,
  MeasureTotals,
  MonthData,
  OutstandingOrder,
  RevenueCategory,
  RevenueCentre,
  RevenueDepartment,
  RevenueMeasures,
  RevenueProduct,
  RevenueStage,
  RevenueUnit,
  SalesReportData,
  Settlement,
  SettlementByCategoryMethod,
  SettlementByMethod,
  SettlementCategoryRow,
  TrendPoint,
} from '../types'

// =============================================================================
// Consolidated (€) view — combines the EUR and USD blocks into one € figure.
//
// FX method (matches IAS 21 / ASC 830 management practice):
//   • Flow figures with a monthly breakdown (KPIs, settlement, trend, the YTD
//     month arrays) are converted at THAT month's average rate, then summed.
//   • YTD-only scalars with no monthly detail (per-centre credit notes, segment
//     YTDs, the YTD credit-note summaries) use the YTD-month average rate.
//   • The 17-month window totals use the window-average rate.
// Each month's rate is researched and locked, so closed months never restate.
// =============================================================================

/** Convert a USD amount to EUR. `rate` is USD per 1 EUR, so EUR = USD / rate. */
const toEur = (usd: number, rate: number) => (rate ? usd / rate : 0)

const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 1)

/** The same month one year earlier ("May-26" → "May-25") — used to convert the
 *  prior-year (YoY) arrays at the prior-year month's locked FX. */
const pyMonth = (m: string) => m.replace(/-(\d{2})$/, (_full, y) => `-${String(Number(y) - 1).padStart(2, '0')}`)

function mergeKpis(e: Kpis, u: Kpis, rate: number): Kpis {
  return {
    grossSale: e.grossSale + toEur(u.grossSale, rate),
    trueSales: e.trueSales + toEur(u.trueSales, rate),
    netSales: e.netSales + toEur(u.netSales, rate),
    revenue: e.revenue + toEur(u.revenue, rate),
    pipeline: e.pipeline + toEur(u.pipeline, rate),
    cashCollected: e.cashCollected + toEur(u.cashCollected, rate),
    outstanding: e.outstanding + toEur(u.outstanding, rate),
    pcCredit: e.pcCredit + toEur(u.pcCredit, rate),
  }
}

function mergeSettlement(e: Settlement, u: Settlement, rate: number): Settlement {
  return {
    grossSale: e.grossSale + toEur(u.grossSale, rate),
    pcCredit: e.pcCredit + toEur(u.pcCredit, rate),
    trueSales: e.trueSales + toEur(u.trueSales, rate),
    card: e.card + toEur(u.card, rate),
    invoiceWeb: e.invoiceWeb + toEur(u.invoiceWeb, rate),
    invoiceQb: e.invoiceQb + toEur(u.invoiceQb, rate),
    cashCollected: e.cashCollected + toEur(u.cashCollected, rate),
    outstanding: e.outstanding + toEur(u.outstanding, rate),
    refunds: e.refunds + toEur(u.refunds, rate),
    netSales: e.netSales + toEur(u.netSales, rate),
  }
}

function mergeMonthData(e: MonthData, u: MonthData | undefined, rate: number): MonthData {
  const c = (ev: number, uv: number | undefined) => ev + toEur(uv ?? 0, rate)
  return {
    grossSale: c(e.grossSale, u?.grossSale),
    trueSales: c(e.trueSales, u?.trueSales),
    netSales: c(e.netSales, u?.netSales),
    revenue: c(e.revenue, u?.revenue),
    pipeline: c(e.pipeline, u?.pipeline),
    cashCollected: c(e.cashCollected, u?.cashCollected),
    outstanding: c(e.outstanding, u?.outstanding),
    pcCredit: c(e.pcCredit, u?.pcCredit),
    card: c(e.card, u?.card),
    invoiceWeb: c(e.invoiceWeb, u?.invoiceWeb),
    invoiceQb: c(e.invoiceQb, u?.invoiceQb),
    refunds: c(e.refunds, u?.refunds),
  }
}

// --- Schema v2: Business unit › Department › Category › Product -------------
// The same FX rules as the legacy centre rows: month arrays convert at each
// month's locked rate (prior year at the prior-year month's rate); YTD-only
// scalars (product YTDs, credit notes) at the YTD average; window scalars at
// the window average. Nodes are merged by id so a category that only exists in
// one currency still appears (zero-filled on the other side).

interface FxCtx {
  ytd: string[]
  rateAt: (m: string) => number
  ytdAvg: number
  winAvg: number
}

function mergeMeasures(e: RevenueMeasures | undefined, u: RevenueMeasures | undefined, fx: FxCtx): RevenueMeasures {
  const { ytd, rateAt } = fx
  const series = (key: 'ord' | 'inv' | 'pipe') =>
    ytd.map((m, i) => (e?.[key]?.[i] ?? 0) + toEur(u?.[key]?.[i] ?? 0, rateAt(m)))
  const ord = series('ord')
  const inv = series('inv')
  const pipe = series('pipe')
  const hasPY = (e?.ordPY?.length ?? 0) > 0 || (u?.ordPY?.length ?? 0) > 0
  const seriesPY = (key: 'ordPY' | 'invPY' | 'pipePY') =>
    ytd.map((m, i) => (e?.[key]?.[i] ?? 0) + toEur(u?.[key]?.[i] ?? 0, rateAt(pyMonth(m))))
  const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0)
  const out: RevenueMeasures = {
    ord,
    inv,
    pipe,
    ordYtd: sum(ord),
    invYtd: sum(inv),
    pipeYtd: sum(pipe),
    pipeline: (e?.pipeline ?? false) || (u?.pipeline ?? false),
  }
  if (hasPY) {
    const ordPY = seriesPY('ordPY')
    const invPY = seriesPY('invPY')
    const pipePY = seriesPY('pipePY')
    Object.assign(out, { ordPY, invPY, pipePY, ordYtdPY: sum(ordPY), invYtdPY: sum(invPY), pipeYtdPY: sum(pipePY) })
  }
  return out
}

/** Union of two id-keyed lists, EUR order first, then USD-only ids. */
function unionIds<T extends { id: string }>(a: T[] | undefined, b: T[] | undefined): string[] {
  const ids = (a ?? []).map((x) => x.id)
  ;(b ?? []).forEach((x) => {
    if (!ids.includes(x.id)) ids.push(x.id)
  })
  return ids
}

function mergeStages(e: RevenueStage[] | undefined, u: RevenueStage[] | undefined, fx: FxCtx): RevenueStage[] | undefined {
  if (!e && !u) return undefined
  return unionIds(e, u).map((id) => {
    const es = e?.find((s) => s.id === id)
    const us = u?.find((s) => s.id === id)
    return { id: id as RevenueStage['id'], name: es?.name ?? us?.name ?? id, ...mergeMeasures(es, us, fx) }
  })
}

/** Products merge by name; the "Other" roll-ups fold into one row and their
 *  name lists are unioned, so each category's list still sums to the category. */
function mergeProducts(e: RevenueProduct[] | undefined, u: RevenueProduct[] | undefined, fx: FxCtx): RevenueProduct[] {
  const map = new Map<string, RevenueProduct>()
  let other: RevenueProduct | null = null
  const add = (ps: RevenueProduct[] | undefined, usd: boolean) =>
    ps?.forEach((p) => {
      const ytdV = usd ? toEur(p.ordYtd, fx.ytdAvg) : p.ordYtd
      const winV = usd ? toEur(p.ordWindow, fx.winAvg) : p.ordWindow
      if (p.other) {
        other = other ?? { name: '', ordYtd: 0, ordWindow: 0, stage: null, legacyClass: null, other: true, products: [] }
        other.ordYtd += ytdV
        other.ordWindow += winV
        p.products?.forEach((n) => {
          if (!other!.products!.includes(n)) other!.products!.push(n)
        })
        return
      }
      const prev = map.get(p.name) ?? { ...p, ordYtd: 0, ordWindow: 0 }
      prev.ordYtd += ytdV
      prev.ordWindow += winV
      map.set(p.name, prev)
    })
  add(e, false)
  add(u, true)
  const rows = [...map.values()].sort((a, b) => b.ordYtd - a.ordYtd)
  if (other) {
    const o = other as RevenueProduct
    o.name = `Other (${o.products!.length} products)`
    rows.push(o)
  }
  return rows
}

function mergeCategories(e: RevenueCategory[] | undefined, u: RevenueCategory[] | undefined, fx: FxCtx, deptYtd: number): RevenueCategory[] {
  return unionIds(e, u).map((id) => {
    const ec = e?.find((c) => c.id === id)
    const uc = u?.find((c) => c.id === id)
    const m = mergeMeasures(ec, uc, fx)
    const stages = mergeStages(ec?.stages, uc?.stages, fx)
    const products = mergeProducts(ec?.products, uc?.products, fx)
    return {
      id,
      name: ec?.name ?? uc?.name ?? id,
      ...m,
      share: deptYtd ? m.ordYtd / deptYtd : 0,
      ...(stages ? { stages } : {}),
      products,
      productCount: products.filter((p) => !p.other).length + (products.find((p) => p.other)?.products?.length ?? 0),
    }
  })
}

function mergeDepartments(e: RevenueDepartment[] | undefined, u: RevenueDepartment[] | undefined, fx: FxCtx, unitYtd: number): RevenueDepartment[] {
  return unionIds(e, u).map((id) => {
    const ed = e?.find((d) => d.id === id)
    const ud = u?.find((d) => d.id === id)
    const m = mergeMeasures(ed, ud, fx)
    const stages = mergeStages(ed?.stages, ud?.stages, fx)
    return {
      id,
      name: ed?.name ?? ud?.name ?? id,
      ...m,
      share: unitYtd ? m.ordYtd / unitYtd : 0,
      ...(stages ? { stages } : {}),
      categories: mergeCategories(ed?.categories, ud?.categories, fx, m.ordYtd),
    }
  })
}

/** Merge the v2 unit trees of both currencies into one € tree. Returns undefined
 *  when neither side carries revenueByUnit (a v1 bundle). */
export function mergeRevenueByUnit(e: RevenueUnit[] | undefined, u: RevenueUnit[] | undefined, fx: FxCtx): RevenueUnit[] | undefined {
  if (!e && !u) return undefined
  return unionIds(e, u).map((id) => {
    const eu = e?.find((x) => x.id === id)
    const uu = u?.find((x) => x.id === id)
    const m = mergeMeasures(eu, uu, fx)
    const creditNotes = (eu?.creditNotes ?? 0) + toEur(uu?.creditNotes ?? 0, fx.ytdAvg)
    return {
      id,
      name: eu?.name ?? uu?.name ?? id,
      ...m,
      creditNotes,
      invAfterCN: m.invYtd - creditNotes,
      departments: mergeDepartments(eu?.departments, uu?.departments, fx, m.ordYtd),
    }
  })
}

// --- Credit notes by Business unit › Department › Category ------------------
// Same rule as creditNotes.byMonth: every month converts at that month's locked
// rate, and the YTD figures are the sum of the converted YTD months — so in the
// Consolidated view departments sum to their unit, units to the revenue table's
// Credit notes row, to the cent.

function mergeCnNode(e: CreditNoteNode | undefined, u: CreditNoteNode | undefined, months: string[], ytdIdx: number[], rateAt: (m: string) => number): CreditNoteNode {
  const series = (key: 'reversal' | 'memo') => months.map((m, i) => (e?.[key]?.[i] ?? 0) + toEur(u?.[key]?.[i] ?? 0, rateAt(m)))
  const reversal = series('reversal')
  const memo = series('memo')
  return {
    id: e?.id ?? u?.id ?? '',
    name: e?.name ?? u?.name ?? '',
    reversal,
    memo,
    reversalYtd: ytdIdx.reduce((t, i) => t + (reversal[i] ?? 0), 0),
    memoYtd: ytdIdx.reduce((t, i) => t + (memo[i] ?? 0), 0),
  }
}

/** Merge the EUR and USD credit-note trees into one € tree (nodes by id). */
export function mergeCreditNotesByHierarchy(
  e: CreditNotesByHierarchy | undefined,
  u: CreditNotesByHierarchy | undefined,
  ytd: string[],
  rateAt: (m: string) => number,
): CreditNotesByHierarchy | undefined {
  // Both currencies must carry the tree, otherwise a Consolidated breakdown would silently miss one side.
  if (!e || !u) return undefined
  const months = e.months
  const ytdIdx = ytd.map((m) => months.indexOf(m)).filter((i) => i >= 0)
  const units: CreditNoteUnitNode[] = unionIds(e.units, u.units).map((uid) => {
    const eu = e.units.find((x) => x.id === uid)
    const uu = u.units.find((x) => x.id === uid)
    const departments: CreditNoteDepartmentNode[] = unionIds(eu?.departments, uu?.departments).map((did) => {
      const ed = eu?.departments.find((x) => x.id === did)
      const ud = uu?.departments.find((x) => x.id === did)
      const categories = unionIds(ed?.categories, ud?.categories).map((cid) =>
        mergeCnNode(ed?.categories.find((x) => x.id === cid), ud?.categories.find((x) => x.id === cid), months, ytdIdx, rateAt),
      )
      return { ...mergeCnNode(ed, ud, months, ytdIdx, rateAt), categories }
    })
    // Keep the classification order with "unallocated" last, whichever side carried it.
    departments.sort((a, b) => Number(a.id === 'unallocated') - Number(b.id === 'unallocated'))
    return { ...mergeCnNode(eu, uu, months, ytdIdx, rateAt), departments }
  })
  const unallocated = [
    ...e.unallocated,
    ...u.unallocated.map((l) => ({ ...l, amount: toEur(l.amount, rateAt(l.month)) })),
  ]
  return { axis: 'inv', months, units, unallocated, ...(e.note ? { note: e.note } : {}) }
}

/** Put the (merged) credit-note tree's YTD figures on the revenue tree: unit and
 *  department creditNotes / invAfterCN, plus the unit's unallocated part. */
function applyCreditNotesToUnits(units: RevenueUnit[], tree: CreditNotesByHierarchy): RevenueUnit[] {
  return units.map((unit) => {
    const cu = tree.units.find((x) => x.id === unit.id)
    if (!cu) return unit
    const unallocated = cu.departments.find((d) => d.id === 'unallocated')?.reversalYtd ?? 0
    return {
      ...unit,
      creditNotes: cu.reversalYtd,
      invAfterCN: unit.invYtd - cu.reversalYtd,
      creditNotesUnallocated: unallocated,
      departments: unit.departments.map((d) => {
        const cn = cu.departments.find((x) => x.id === d.id)?.reversalYtd ?? 0
        return { ...d, creditNotes: cn, invAfterCN: d.invYtd - cn }
      }),
    }
  })
}

/** Merge settlement-by-category across currencies per month and method (USD at
 *  the month's locked rate), keeping the Outstanding chase lists expandable. */
export function mergeSettlementByCategory(
  e: Record<string, SettlementByCategoryMethod> | undefined,
  u: Record<string, SettlementByCategoryMethod> | undefined,
  rateAt: (m: string) => number,
): Record<string, SettlementByCategoryMethod> | undefined {
  const months = new Set([...Object.keys(e ?? {}), ...Object.keys(u ?? {})])
  if (!months.size) return undefined
  const methods: (keyof SettlementByCategoryMethod)[] = ['card', 'invoiceWeb', 'invoiceQb', 'outstanding']
  const out: Record<string, SettlementByCategoryMethod> = {}
  for (const mo of months) {
    const r = rateAt(mo)
    const em = e?.[mo] ?? {}
    const um = u?.[mo] ?? {}
    const byMethod: SettlementByCategoryMethod = {}
    for (const method of methods) {
      const map = new Map<string, SettlementCategoryRow>()
      const acc = (x: SettlementCategoryRow, rate: number | null) => {
        const prev = map.get(x.categoryKey) ?? { ...x, amount: 0, orders: [] }
        prev.amount += rate == null ? x.amount : toEur(x.amount, rate)
        if (x.orders) prev.orders!.push(...x.orders.map((o) => (rate == null ? o : { ...o, value: toEur(o.value, rate) })))
        map.set(x.categoryKey, prev)
      }
      ;(em[method] ?? []).forEach((x) => acc(x, null))
      ;(um[method] ?? []).forEach((x) => acc(x, r))
      if (map.size) {
        byMethod[method] = [...map.values()]
          .map((row) => (row.orders!.length ? { ...row, orders: row.orders!.sort((a, b) => b.value - a.value) } : { ...row, orders: undefined }))
          .sort((a, b) => b.amount - a.amount)
      }
    }
    out[mo] = byMethod
  }
  return out
}

/** Build the consolidated (€) CurrencyData from EUR + USD at the locked rates. */
export function buildConsolidated(report: SalesReportData): CurrencyData {
  const e = report.data.EUR
  const u = report.data.USD
  const fx = report.fxRates
  const months = report.months
  const ytd = e.monthsInYtd

  const rateAt = (m: string) => fx[m] ?? 1
  const ytdAvg = mean(ytd.map(rateAt))
  const winAvg = mean(months.map(rateAt))

  // KPIs cover the current month (last trend month) and the prior month.
  const curM = months[months.length - 1]
  const prevM = months[months.length - 2]

  const kpis: KpiSet = {
    ...mergeKpis(e.kpis, u.kpis, rateAt(curM)),
    priorMonth: mergeKpis(e.kpis.priorMonth, u.kpis.priorMonth, rateAt(prevM)),
  }

  const settlement = mergeSettlement(e.settlement, u.settlement, rateAt(curM))

  const trend: TrendPoint[] = e.trend.map((pt, i) => {
    const up = u.trend[i]
    const r = rateAt(pt.month)
    return {
      month: pt.month,
      gross: pt.gross + toEur(up?.gross ?? 0, r),
      afterCN: pt.afterCN + toEur(up?.afterCN ?? 0, r),
    }
  })

  // Revenue centres: union by id (USD has fewer centres than EUR).
  const eById = new Map(e.revenueByCentre.map((c) => [c.id, c]))
  const uById = new Map(u.revenueByCentre.map((c) => [c.id, c]))
  const ids = e.revenueByCentre.map((c) => c.id)
  u.revenueByCentre.forEach((c) => {
    if (!ids.includes(c.id)) ids.push(c.id)
  })

  const revenueByCentre: RevenueCentre[] = ids.map((id) => {
    const ec = eById.get(id)
    const uc = uById.get(id)
    const ord = ytd.map((m, i) => (ec?.ord[i] ?? 0) + toEur(uc?.ord[i] ?? 0, rateAt(m)))
    const inv = ytd.map((m, i) => (ec?.inv[i] ?? 0) + toEur(uc?.inv[i] ?? 0, rateAt(m)))
    const pipe = ytd.map((m, i) => (ec?.pipe[i] ?? 0) + toEur(uc?.pipe[i] ?? 0, rateAt(m)))
    const ordYtd = ord.reduce((a, b) => a + b, 0)
    const invYtd = inv.reduce((a, b) => a + b, 0)
    const pipeYtd = pipe.reduce((a, b) => a + b, 0)
    const creditNotes = (ec?.creditNotes ?? 0) + toEur(uc?.creditNotes ?? 0, ytdAvg)

    // Prior-year (YoY) arrays — USD converted at the PRIOR-year month's rate so a
    // strong/weak dollar last year is reflected. Omitted when the bundle lacks them.
    const hasPY = (ec?.ordPY?.length ?? 0) > 0 || (uc?.ordPY?.length ?? 0) > 0
    const ordPY = hasPY ? ytd.map((m, i) => (ec?.ordPY?.[i] ?? 0) + toEur(uc?.ordPY?.[i] ?? 0, rateAt(pyMonth(m)))) : undefined
    const invPY = hasPY ? ytd.map((m, i) => (ec?.invPY?.[i] ?? 0) + toEur(uc?.invPY?.[i] ?? 0, rateAt(pyMonth(m)))) : undefined
    const pipePY = hasPY ? ytd.map((m, i) => (ec?.pipePY?.[i] ?? 0) + toEur(uc?.pipePY?.[i] ?? 0, rateAt(pyMonth(m)))) : undefined

    // Merge Segment → Product Class slices by key, then recompute shares. Monthly Ord/Inv
    // by class are summed with per-month FX (USD converted at each month's locked rate), so
    // the drill-down's per-month figures stay correct in the Consolidated view.
    const segMap = new Map<string, { segment: string; productClass: string; ytd: number; ord: number[]; inv: number[]; ordPY: number[]; invPY: number[]; hasPY: boolean }>()
    const addSegs = (segs: CentreSegment[] | undefined, usd: boolean) => {
      segs?.forEach((s) => {
        const key = `${s.segment}|${s.productClass}`
        const prev =
          segMap.get(key) ?? { segment: s.segment, productClass: s.productClass, ytd: 0, ord: ytd.map(() => 0), inv: ytd.map(() => 0), ordPY: ytd.map(() => 0), invPY: ytd.map(() => 0), hasPY: false }
        prev.ytd += usd ? toEur(s.ytd, ytdAvg) : s.ytd
        ytd.forEach((m, i) => {
          prev.ord[i] += usd ? toEur(s.ord?.[i] ?? 0, rateAt(m)) : s.ord?.[i] ?? 0
          prev.inv[i] += usd ? toEur(s.inv?.[i] ?? 0, rateAt(m)) : s.inv?.[i] ?? 0
          // Prior year converts at the prior-year month's rate.
          prev.ordPY[i] += usd ? toEur(s.ordPY?.[i] ?? 0, rateAt(pyMonth(m))) : s.ordPY?.[i] ?? 0
          prev.invPY[i] += usd ? toEur(s.invPY?.[i] ?? 0, rateAt(pyMonth(m))) : s.invPY?.[i] ?? 0
        })
        if (s.ordPY || s.invPY) prev.hasPY = true
        segMap.set(key, prev)
      })
    }
    addSegs(ec?.segments, false)
    addSegs(uc?.segments, true)
    const segTotal = [...segMap.values()].reduce((a, s) => a + s.ytd, 0) || 1
    const segments: CentreSegment[] = [...segMap.values()].map((s) => ({
      segment: s.segment,
      productClass: s.productClass,
      ytd: s.ytd,
      share: s.ytd / segTotal,
      ord: s.ord,
      inv: s.inv,
      ...(s.hasPY ? { ordPY: s.ordPY, invPY: s.invPY } : {}),
    }))

    return {
      id,
      name: ec?.name ?? uc?.name ?? id,
      ord,
      inv,
      pipe,
      ordYtd,
      invYtd,
      pipeYtd,
      creditNotes,
      invAfterCN: invYtd - creditNotes,
      pipeline: (ec?.pipeline ?? false) || (uc?.pipeline ?? false),
      segments,
      ...(hasPY
        ? {
            ordPY,
            invPY,
            pipePY,
            ordYtdPY: ordPY!.reduce((a, b) => a + b, 0),
            invYtdPY: invPY!.reduce((a, b) => a + b, 0),
            pipeYtdPY: pipePY!.reduce((a, b) => a + b, 0),
          }
        : {}),
    }
  })

  const totals: MeasureTotals = {
    ord: e.totals.ord + toEur(u.totals.ord, winAvg),
    inv: e.totals.inv + toEur(u.totals.inv, winAvg),
    pipe: e.totals.pipe + toEur(u.totals.pipe, winAvg),
    afterWindow: e.totals.afterWindow + toEur(u.totals.afterWindow, winAvg),
    priorYearDelivered:
      (e.totals.priorYearDelivered ?? 0) + toEur(u.totals.priorYearDelivered ?? 0, winAvg),
  }

  const cnByMonth: CreditNoteMonth[] = e.creditNotes.byMonth.map((m, i) => {
    const um = u.creditNotes.byMonth[i]
    const r = rateAt(m.month)
    // Merge the per-centre breakdown (USD converted to €), keyed by centre.
    const cMap = new Map<string, { centre: string; inv: number; ord: number; group: 'reversal' | 'memo' }>()
    const addCentres = (cs: typeof m.centres, rate: number | null) =>
      cs?.forEach((c) => {
        const prev = cMap.get(c.centre) ?? { centre: c.centre, inv: 0, ord: 0, group: c.group }
        prev.inv += rate == null ? c.inv : toEur(c.inv, rate)
        prev.ord += rate == null ? c.ord : toEur(c.ord, rate)
        cMap.set(c.centre, prev)
      })
    addCentres(m.centres, null)
    addCentres(um?.centres, r)
    return {
      month: m.month,
      ordReversal: m.ordReversal + toEur(um?.ordReversal ?? 0, r),
      invReversal: m.invReversal + toEur(um?.invReversal ?? 0, r),
      ordMemo: m.ordMemo + toEur(um?.ordMemo ?? 0, r),
      invMemo: m.invMemo + toEur(um?.invMemo ?? 0, r),
      centres: cMap.size ? [...cMap.values()] : undefined,
    }
  })
  const cnDetail: CreditNoteDetail[] = [
    ...e.creditNotes.detail,
    ...u.creditNotes.detail.map((d) => ({
      ...d,
      amount: toEur(d.amount, fx[d.orderMonth] ?? fx[d.raisedMonth] ?? ytdAvg),
    })),
  ]
  const cnByHierarchy = mergeCreditNotesByHierarchy(e.creditNotes.byHierarchy, u.creditNotes.byHierarchy, ytd, rateAt)
  const creditNotes: CreditNotes = {
    reversalInvYtd: e.creditNotes.reversalInvYtd + toEur(u.creditNotes.reversalInvYtd, ytdAvg),
    reversalOrdYtd: e.creditNotes.reversalOrdYtd + toEur(u.creditNotes.reversalOrdYtd, ytdAvg),
    memoInvYtd: e.creditNotes.memoInvYtd + toEur(u.creditNotes.memoInvYtd, ytdAvg),
    memoOrdYtd: e.creditNotes.memoOrdYtd + toEur(u.creditNotes.memoOrdYtd, ytdAvg),
    byMonth: cnByMonth,
    detail: cnDetail,
    ...(cnByHierarchy ? { byHierarchy: cnByHierarchy } : {}),
  }

  const byMonth = e.byMonth.map((bm, i) => {
    const um = u.byMonth[i]
    const r = rateAt(bm.month)
    return {
      month: bm.month,
      order: mergeMonthData(bm.order, um?.order, r),
      recognition: mergeMonthData(bm.recognition, um?.recognition, r),
    }
  })

  // Settlement-by-class: merge EUR + USD (USD converted to €) per month, per method.
  let settlementByClass: Record<string, SettlementByMethod> | undefined
  const sbcMonths = new Set([
    ...Object.keys(e.settlementByClass ?? {}),
    ...Object.keys(u.settlementByClass ?? {}),
  ])
  if (sbcMonths.size) {
    settlementByClass = {}
    const methods: (keyof SettlementByMethod)[] = ['card', 'invoiceWeb', 'invoiceQb', 'outstanding']
    for (const mo of sbcMonths) {
      const r = rateAt(mo)
      const em = e.settlementByClass?.[mo] ?? {}
      const um = u.settlementByClass?.[mo] ?? {}
      const out: SettlementByMethod = {}
      for (const method of methods) {
        // Outstanding rows carry the per-order chase list; merge orders across currencies
        // (USD order values converted at the month's locked rate) so the Consolidated drill
        // stays expandable. Other methods carry no orders, so this is amount-only for them.
        const map = new Map<string, { amount: number; orders: OutstandingOrder[] }>()
        const acc = (x: { productClass: string; amount: number; orders?: OutstandingOrder[] }, rate: number | null) => {
          const prev = map.get(x.productClass) ?? { amount: 0, orders: [] }
          prev.amount += rate == null ? x.amount : toEur(x.amount, rate)
          if (x.orders) prev.orders.push(...x.orders.map((o) => (rate == null ? o : { ...o, value: toEur(o.value, rate) })))
          map.set(x.productClass, prev)
        }
        ;(em[method] ?? []).forEach((x) => acc(x, null))
        ;(um[method] ?? []).forEach((x) => acc(x, r))
        if (map.size) {
          out[method] = [...map.entries()]
            .map(([productClass, v]) => ({ productClass, amount: v.amount, ...(v.orders.length ? { orders: v.orders.sort((a, b) => b.value - a.value) } : {}) }))
            .sort((a, b) => b.amount - a.amount)
        }
      }
      settlementByClass[mo] = out
    }
  }

  // Schema v2 tree + settlement by category, same FX rules.
  const fxCtx: FxCtx = { ytd, rateAt, ytdAvg, winAvg }
  const mergedUnits = mergeRevenueByUnit(e.revenueByUnit, u.revenueByUnit, fxCtx)
  // With the credit-note tree, unit and department credit notes come from it (month-rate),
  // so they tie to the table's Credit notes row; without it, the unit-level YTD-average figure stays.
  const revenueByUnit = mergedUnits && cnByHierarchy ? applyCreditNotesToUnits(mergedUnits, cnByHierarchy) : mergedUnits
  const settlementByCategory = mergeSettlementByCategory(e.settlementByCategory, u.settlementByCategory, rateAt)

  return {
    kpis, settlement, trend, monthsInYtd: ytd, revenueByCentre, totals, creditNotes, byMonth,
    ...(settlementByClass ? { settlementByClass } : {}),
    ...(revenueByUnit ? { revenueByUnit } : {}),
    ...(settlementByCategory ? { settlementByCategory } : {}),
  }
}

/** Current-month rate (USD per 1 EUR) for display in the consolidated note. */
export function currentRate(report: SalesReportData): number {
  return report.fxRates[report.months[report.months.length - 1]] ?? 1
}
